import { useCallback, useEffect, useState } from 'react';
import { getClientProfile } from 'api';
import { clearPhoneNumberFormat } from 'helper';
import { ClientInsightData, mapClientProfileToManagerInsight, mapClientProfileToOperatorInsight } from './mapper';

interface IClientProfileState {
  loading: boolean;
  error: boolean;
  operator: ClientInsightData | null;
  manager: ClientInsightData | null;
  reload: () => void;
}

/**
 * Загрузка единого профиля клиента по номеру телефона: один запрос /ai/client-profile
 * отдаёт обе карточки (оператор + менеджер). Запрос уходит только при enabled=true и
 * корректном номере (11 цифр КЗ). Долгий (8–120 сек) — пробрасывайте loading в карточку.
 */
export const useClientProfile = (phone: string | null | undefined, enabled: boolean): IClientProfileState => {
  const [operator, setOperator] = useState<ClientInsightData | null>(null);
  const [manager, setManager] = useState<ClientInsightData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    const cleaned = clearPhoneNumberFormat(phone ?? '', '+7');
    if (!enabled || cleaned.length !== 10) {
      setOperator(null);
      setManager(null);
      setError(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);
    setOperator(null);
    setManager(null);

    getClientProfile(`+7${cleaned}`)
      .then((response) => {
        if (cancelled) return;
        setOperator(mapClientProfileToOperatorInsight(response));
        setManager(mapClientProfileToManagerInsight(response));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [phone, enabled]);

  useEffect(() => load(), [load]);

  return { loading, error, operator, manager, reload: load };
};
